var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { getSelectOptionByValue, isGrouped, Select, } from "../../Select/Select";
import { FormFieldWrapper } from "../FormFieldWrapper/FormFieldWrapper";
import { useUniqueId } from "../../../util/useUniqueId";
import { GenericFormContext } from "../GenericFormContext";
import { FormFieldGroupContext } from "../FormFieldGroup";
export const getEmptyMessage = ({ options, noOptionsMessage = "", noMatchesMessage = "", }) => ((options === null || options === void 0 ? void 0 : options.length) === 0 ? noOptionsMessage : noMatchesMessage);
/**
 * <br /> <br />
 * ## Description
 * The `FormSelect` component allows the user to select an option from a dropdown list.
 * It renders a select input field with customizable options.
 */
export const FormSelect = (_a) => {
    var { ariaLabel, autoFocus = false, cacheOptions, components, creatable, defaultOptions, disabled = false, filterOptions, helperText, isClearable = false, isMulti = false, isSearchable, label, labelWidth, loading = false, loadOptions, multiLine, name, noOptionsMessage, noMatchesMessage, onChange: onChangeHandler, onInputChange: onInputChangeHandler, openMenuOnFocus, options, placeholder = "", readOnly = false, registerProps, required = false, showLabel = true, showLoadingState = false, value: defaultValue } = _a, rest = __rest(_a, ["ariaLabel", "autoFocus", "cacheOptions", "components", "creatable", "defaultOptions", "disabled", "filterOptions", "helperText", "isClearable", "isMulti", "isSearchable", "label", "labelWidth", "loading", "loadOptions", "multiLine", "name", "noOptionsMessage", "noMatchesMessage", "onChange", "onInputChange", "openMenuOnFocus", "options", "placeholder", "readOnly", "registerProps", "required", "showLabel", "showLoadingState", "value"]);
    const id = useUniqueId();
    const descriptorId = useUniqueId();
    const { setValue, watch, getFieldState, formState } = useFormContext();
    const { invalid } = getFieldState(name, formState);
    // Watch for changes to the uncontrolled input
    // and update with subscription changes
    const latestValue = watch(name);
    const { editIsAllowed } = useContext(GenericFormContext);
    const isReadOnly = readOnly === true || editIsAllowed === false;
    const { disabled: disabledFromFormFieldGroup } = useContext(FormFieldGroupContext);
    const disabledState = disabledFromFormFieldGroup || disabled;
    // Value transformation run on every value change
    useEffect(() => {
        // Wait until the `options` have loaded before setting a value.
        if (loading === false) {
            const selectedOption = getSelectOptionByValue(options, latestValue);
            // There is no option matching the desired value
            if (!selectedOption) {
                let firstOption;
                if (options === null || options === void 0 ? void 0 : options.length) {
                    [firstOption] = isGrouped(options[0]) ? options[0].options : options;
                }
                // Clear the value if the select is clearable,
                // otherwise select the first option which is the same behaviour as a native browser select component
                if (latestValue && firstOption && !isClearable && !disabledState) {
                    if (defaultValue) {
                        setValue(name, isMulti ? [defaultValue] : defaultValue, { shouldDirty: false });
                    }
                    else {
                        setValue(name, isMulti ? [firstOption.value] : firstOption.value, { shouldDirty: false });
                    }
                }
                else if (defaultValue && latestValue !== "") {
                    setValue(name, defaultValue, { shouldDirty: false });
                }
            }
        }
    }, [latestValue, isClearable, setValue, name, disabledState, options, loading, defaultValue, isMulti]);
    // Initial setup for values outside of the options list
    useEffect(() => {
        const selectedOption = getSelectOptionByValue(options, latestValue);
        // If the select is not async, there is no option matching the desired value and the options list is not empty
        if (!selectedOption && loading === false && (options === null || options === void 0 ? void 0 : options.length)) {
            // Clear the value if the select is clearable
            if (isClearable) {
                setValue(name, "", { shouldDirty: false });
                // Otherwise select the first option which is the same behaviour as a native browser select component
            }
            else {
                const [firstOption] = isGrouped(options[0]) ? options[0].options : options;
                setValue(name, firstOption.value, { shouldDirty: false });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We run it once on mount
    }, []);
    const noOptionsText = (options === null || options === void 0 ? void 0 : options.length) ? getEmptyMessage({ noMatchesMessage, noOptionsMessage, options }) : "";
    return (React.createElement(FormFieldWrapper, Object.assign({ descriptorId: descriptorId, helperText: helperText, label: showLabel ? label : undefined, labelHtmlFor: id, labelWidth: labelWidth, multiLine: multiLine, name: name, required: required || Boolean(registerProps === null || registerProps === void 0 ? void 0 : registerProps.required) }, rest),
        React.createElement(Controller, Object.assign({ name: name, defaultValue: defaultValue }, (disabledState ? { disabled: true } : {}), { rules: registerProps, render: ({ field: { disabled: disabledFromField, onChange, onBlur, value } }) => {
                let currentSelectValue = value;
                // For clearable inputs, we have to force `react-select` & `react-hook-form` to have the same state
                if (isClearable && latestValue === undefined) {
                    currentSelectValue = undefined;
                }
                // Handle creatable options: if we have a value but no matching option in the options array,
                // create an option object that matches the format the Select component expects, so that the new value is displayed in the selection box
                if (Array.isArray(currentSelectValue) && creatable) {
                    currentSelectValue = currentSelectValue.map((val) => {
                        const foundOption = getSelectOptionByValue(options, val);
                        if (!foundOption) {
                            return {
                                label: val,
                                value: val,
                            };
                        }
                        return foundOption;
                    });
                }
                return (React.createElement(Select, { "aria-describedby": helperText ? descriptorId : undefined, "aria-label": ariaLabel || label, autoFocus: autoFocus, cacheOptions: cacheOptions, components: components, creatable: creatable, "data-testid": "FormSelect", defaultOptions: defaultOptions, disabled: disabledFromField, filterOptions: filterOptions, inputId: id, isError: invalid, isClearable: isClearable, isMulti: isMulti, isSearchable: isSearchable, loadOptions: loadOptions, noOptionsMessage: noOptionsText, onBlur: onBlur, onChange: (option) => {
                        if (isMulti) {
                            const selectedValues = option.length ? option.map((o) => o.value) : [];
                            onChange(selectedValues);
                        }
                        else {
                            onChange(option ? option === null || option === void 0 ? void 0 : option.value : "");
                        }
                        if (onChangeHandler) {
                            onChangeHandler(option);
                        }
                    }, onInputChange: onInputChangeHandler, openMenuOnFocus: openMenuOnFocus, options: options, placeholder: placeholder, readOnly: isReadOnly, 
                    // When the field has validation rules, we want the Raect Hook Form to validate the field not the browser.
                    required: required && !(registerProps === null || registerProps === void 0 ? void 0 : registerProps.required), value: currentSelectValue, showLoadingState: showLoadingState }));
            } }))));
};
